import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PostArtwork from './PostArtwork';
import PricingPlans from './PricingPlans';
import './LandingPage.css';

const {
  FiArrowRight,
  FiCheck,
  FiLayers,
  FiMessageSquare,
  FiPenTool,
  FiRefreshCw,
  FiShield,
  FiSparkles,
  FiTarget
} = FiIcons;

const steps = [
  {
    number: '01',
    icon: FiMessageSquare,
    title: 'Type an idea',
    text: 'Start with one thought, prompt, or piece of raw material.'
  },
  {
    number: '02',
    icon: FiSparkles,
    title: 'Draft the post',
    text: 'DalePost shapes it into polished copy and on-brand creative.'
  },
  {
    number: '03',
    icon: FiShield,
    title: 'Review & approve',
    text: 'Make the final call before anything is ready to publish.'
  },
  {
    number: '04',
    icon: FiArrowRight,
    title: 'Post it',
    text: 'Copy the right caption for each platform and keep moving.'
  }
];

const features = [
  {
    icon: FiLayers,
    title: 'Every brand, one workspace',
    text: 'Switch between brands without losing their distinct voices, colors, or creative direction.'
  },
  {
    icon: FiPenTool,
    title: 'On-brand by default',
    text: 'Turn your existing brand personality into consistent visual posts and captions.'
  },
  {
    icon: FiShield,
    title: 'You stay in control',
    text: 'Review every draft, leave feedback, and approve only what feels right.'
  },
  {
    icon: FiRefreshCw,
    title: 'A record of what works',
    text: 'Learnings keeps approved ideas and rejection notes together for better creative judgment.'
  },
  {
    icon: FiTarget,
    title: 'Made for every platform',
    text: 'See Instagram, LinkedIn, and Facebook caption variants side by side.'
  }
];

const heroPost = {
  headline: 'Resilience is designed, not discovered.',
  supporting: 'Strong foundations create options when conditions change.',
  image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=85'
};

function LandingPage({ brand, onEnterApp }) {
  return (
    <div className="landing-page">
      <LandingNav onEnterApp={onEnterApp} />

      <main>
        <section className="landing-hero">
          <div className="hero-copy">
            <span className="landing-kicker">
              <i />
              Content, with intention
            </span>
            <h1>From one thought to a post worth <em>sharing.</em></h1>
            <p>
              DalePost turns a one-line idea into a finished, on-brand social
              post — so you can spend less time designing and more time building
              what matters.
            </p>
            <div className="hero-actions">
              <button
                className="landing-primary"
                style={{ background: brand.primary }}
                onClick={onEnterApp}
              >
                Try DalePost free
                <SafeIcon icon={FiArrowRight} />
              </button>
              <a href="#how-it-works" className="landing-text-link">
                See how it works
              </a>
            </div>
            <div className="hero-proof">
              <span><SafeIcon icon={FiCheck} /> No design experience needed</span>
              <span><SafeIcon icon={FiCheck} /> Review before you publish</span>
            </div>
          </div>

          <div className="hero-visual">
            <div className="hero-visual-note">A finished post, from one idea</div>
            <div className="hero-artwork">
              <PostArtwork post={heroPost} brand={brand} />
            </div>
            <div className="hero-caption-note">
              <span>Caption variants</span>
              <strong>Instagram · LinkedIn · Facebook</strong>
            </div>
          </div>
        </section>

        <section className="trust-strip">
          <span>For founders who are building in public</span>
          <span>For teams with more than one voice</span>
          <span>For brands that want to stay consistent</span>
        </section>

        <HowItWorks />
        <FeatureHighlights />
        <PricingPlans onEnterApp={onEnterApp} />

        <section className="landing-final-cta">
          <div>
            <span className="landing-kicker">Make the next post easier</span>
            <h2>Your best ideas deserve a better workflow.</h2>
          </div>
          <button
            className="landing-primary light-button"
            onClick={onEnterApp}
          >
            Get started
            <SafeIcon icon={FiArrowRight} />
          </button>
        </section>
      </main>

      <LandingFooter onEnterApp={onEnterApp} />
    </div>
  );
}

function LandingNav({ onEnterApp }) {
  return (
    <header className="landing-nav">
      <button className="landing-logo" onClick={() => window.scrollTo(0, 0)}>
        <span>D</span>
        DalePost
      </button>
      <nav>
        <a href="#how-it-works">How it works</a>
        <a href="#features">Features</a>
        <a href="#pricing">Pricing</a>
      </nav>
      <button className="nav-cta" onClick={onEnterApp}>
        Get started
        <SafeIcon icon={FiArrowRight} />
      </button>
    </header>
  );
}

function HowItWorks() {
  return (
    <section className="landing-section how-section" id="how-it-works">
      <SectionHeading
        eyebrow="A calmer way to create"
        title={<>The whole process, <em>simplified.</em></>}
        text="DalePost keeps the creative workflow focused, from the first spark to the final caption."
      />
      <div className="steps-grid">
        {steps.map((step) => (
          <article className="step-card" key={step.number}>
            <div className="step-topline">
              <span>{step.number}</span>
              <SafeIcon icon={step.icon} />
            </div>
            <h3>{step.title}</h3>
            <p>{step.text}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function FeatureHighlights() {
  return (
    <section className="landing-section feature-section" id="features">
      <SectionHeading
        eyebrow="More than a blank canvas"
        title={<>Consistency without <em>the busywork.</em></>}
        text="Everything you need to make thoughtful content a repeatable part of running your business."
      />
      <div className="feature-grid">
        {features.map((feature) => (
          <article className="feature-card" key={feature.title}>
            <span className="feature-icon">
              <SafeIcon icon={feature.icon} />
            </span>
            <h3>{feature.title}</h3>
            <p>{feature.text}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function SectionHeading({ eyebrow, title, text }) {
  return (
    <div className="landing-section-heading">
      <span className="landing-kicker">{eyebrow}</span>
      <h2>{title}</h2>
      <p>{text}</p>
    </div>
  );
}

function LandingFooter({ onEnterApp }) {
  return (
    <footer className="landing-footer">
      <button className="landing-logo" onClick={onEnterApp}>
        <span>D</span>
        DalePost
      </button>
      <div className="footer-links">
        <a href="#features">Features</a>
        <a href="#pricing">Pricing</a>
        <a href="mailto:hello@dalepost.example">Contact</a>
      </div>
      <p>© 2025 DalePost. Built for better ideas.</p>
    </footer>
  );
}

export default LandingPage;